import React, { createContext, useContext, useState, ReactNode } from "react";

interface TokenContextType {
  loginToken: string | null;      // nLoginToken
  refreshToken: string | null;    // nRefreshToken
  serviceToken: string | null;    // X-Csrf-Token
  loginExpireAt: number | null;   // loginExpireAt
  refreshExpireAt: number | null; // refreshExpireAt
  registerToken: string | null;   // registerToken
  setTokens: (
    loginToken: string,
    refreshToken: string,
    serviceToken: string,
    loginExpireAt: number,
    refreshExpireAt: number,
  ) => void;
  clearTokens: () => void;
  setRegisterToken: (registerToken: string) => void;
}

const TokenContext = createContext<TokenContextType>({
  loginToken: null,
  refreshToken: null,
  serviceToken: null,
  loginExpireAt: null,
  refreshExpireAt: null,
  registerToken: null,
  setTokens: () => {},
  clearTokens: () => {},
  setRegisterToken: () => {},
});

export const useToken = () => useContext(TokenContext);

export const TokenProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [loginToken, setLoginToken] = useState<string | null>(() => localStorage.getItem("loginToken"));
  const [refreshToken, setRefreshToken] = useState<string | null>(() => localStorage.getItem("refreshToken"));
  const [serviceToken, setServiceToken] = useState<string | null>(() => localStorage.getItem("serviceToken"));
  const [registerToken, setRegisterToken] = useState<string | null>(() => localStorage.getItem("registerToken"));
  const [loginExpireAt, setLoginExpireAt] = useState<number | null>(() => {
    const value = localStorage.getItem("loginExpireAt");
    return value ? parseInt(value, 10) : null;
  });
  const [refreshExpireAt, setRefreshExpireAt] = useState<number | null>(() => {
    const value = localStorage.getItem("refreshExpireAt");
    return value ? parseInt(value, 10) : null;
  });

  const setTokens = (
    newLoginToken: string,
    newRefreshToken: string,
    newServiceToken: string,
    newLoginExpireAt: number,
    newRefreshExpireAt: number,
  ) => {
    setLoginToken(newLoginToken);
    setRefreshToken(newRefreshToken);
    setServiceToken(newServiceToken);
    setLoginExpireAt(newLoginExpireAt);
    setRefreshExpireAt(newRefreshExpireAt);

    localStorage.setItem("loginToken", newLoginToken);
    localStorage.setItem("refreshToken", newRefreshToken);
    localStorage.setItem("serviceToken", newServiceToken);
    localStorage.setItem("loginExpireAt", newLoginExpireAt.toString());
    localStorage.setItem("refreshExpireAt", newRefreshExpireAt.toString());
  };

  const setRegisterTokenOnly = (newRegisterToken: string) => {
    setRegisterToken(newRegisterToken);
    localStorage.setItem("registerToken", newRegisterToken);
  }

  const clearTokens = () => {
    setLoginToken(null);
    setRefreshToken(null);
    setServiceToken(null);
    setLoginExpireAt(null);
    setRefreshExpireAt(null);
    setRegisterToken(null);

    localStorage.removeItem("loginToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("serviceToken");
    localStorage.removeItem("loginExpireAt");
    localStorage.removeItem("refreshExpireAt");
    localStorage.removeItem("registerToken");
  };

  return (
    <TokenContext.Provider
      value={{
        loginToken,
        refreshToken,
        serviceToken,
        loginExpireAt,
        refreshExpireAt,
        registerToken,
        setTokens,
        clearTokens,
        setRegisterToken: setRegisterTokenOnly,
      }}
    >
      {children}
    </TokenContext.Provider>
  );
};